/**
 * Billing API Service
 *
 * Billing, invoicing, WIP ledger and invoice-template API functions.
 * Split out of data-service.ts; re-exported from '@/lib/data-service'
 * so existing import sites keep working.
 */

import type { CategoryType } from '@/types/category.types';
import type { RevisionReasonCategoryConfig } from '@/types/resource-budget-settings.types';
import type { ProjectTemplate, CreateTemplateData } from '@/types/template.types';
import { getBudgetHealthThresholds, deriveProjectHealth } from './budget-health-thresholds-storage';
import { calculateGP, calculateBudgetGP } from './gp-calculation';
import { computeEffectiveCompletionPercent } from './completion';
import * as mockData from './mock-data';
import * as tourMockData from './tour-mock-data';
import { getBudgetDataByProjectId as getMockBudgetData } from './mock-data/budget-mock-data';
import { apiFetch } from './api-error-handler';
import { authFetch } from './auth-fetch';
import { getApiBaseUrl, getDataMode } from './data-service';
import type { WipQueryOptions } from './wip-filtering';
import { DEMO_INVOICES } from './demo-invoices';

// ============================================================
// Billing API Functions
// ============================================================

export interface BillingWipItem {
  id: number;
  projectId: number;
  sourceType: number; // 1=Timesheet, 2=Expense, 3=Disbursement
  sourceId: number;
  taskId?: number;
  employeeId?: number;
  date: string;
  description: string;
  originalQuantity: number;
  originalRate: number;
  originalAmount: number;
  quantity: number;
  unit: string;
  rate: number;
  netAmount: number;
  taxAmount: number;
  grossAmount: number;
  costRate?: number;
  status: number; // 0=Unbilled, 1=Draft, 2=Invoiced, 3=WriteOff, 4=Void
  isWriteOff: boolean;
  invoiceId?: number;
  billedDate?: string;
  createdBy?: string;
  updatedBy?: string;
  postedAt?: string;
  postedBy?: string;
  employeeName?: string;
  taskName?: string;
  externalCostCentreCode?: string;
  roleName?: string;
}

export interface BillingInvoice {
  id: number;
  projectId: number;
  milestoneId?: number; // FK to BillingMilestone; undefined for T&M invoices
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  description?: string;
  notes?: string;
  status: number; // 0=Draft, 1=Sent, 2=PartiallyPaid, 3=Paid, 4=Overdue, 5=Void, 6=WriteOff, 7=Credited, 8=PartiallyCredited
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  grossTotal: number;
  accessFinancialsId?: string;
  sentDate?: string;
  paidDate?: string;
  paidAmount?: number;
  voidDate?: string;
  voidReason?: string;
  createdBy?: string;
  updatedBy?: string;
  postedAt?: string;
  postedBy?: string;
  draftEmailTo?: string;
  draftEmailCc?: string;
  draftEmailBcc?: string;
  draftEmailSubject?: string;
  draftEmailBody?: string;
  draftEmailTemplateId?: string;
}

export interface BillingInvoiceLineItem {
  id: number;
  invoiceId: number;
  wipLedgerId?: number;
  sortOrder: number;
  description: string;
  quantity: number;
  /** Per-unit bill rate. Lines written before the rate was persisted store `0`. */
  rate: number;
  netAmount: number;
  taxAmount: number;
  grossAmount: number;
  financialsTransactionId?: number;
  createdBy?: string;
  updatedBy?: string;
  wipDate?: string;
  wipResourceName?: string;
  wipRoleName?: string;
  /** Human-readable WIP source transaction type (e.g. 'Time', 'Purchase', 'Invoice', 'Journal', 'VAT Journal'). */
  type?: string;
  wipCostCentreCode?: string;
  wipCostCentreName?: string;
  wipPhase?: string;
  wipTask?: string;
}

const INVOICE_STATUS_MAP: Record<number, string> = {
  0: 'Draft',
  1: 'Sent',
  2: 'PartiallyPaid',
  3: 'Paid',
  4: 'Overdue',
  5: 'Void',
  6: 'WriteOff',
  7: 'Credited',
  8: 'PartiallyCredited',
};

const WIP_SOURCE_TYPE_MAP: Record<number, string> = {
  1: 'Time',
  2: 'Expense',
  3: 'Purchase',
  4: 'Invoice',
  5: 'Credit Note',
  6: 'Adjustment',
  7: 'Journal',
  8: 'Sales',
  9: 'Miscellaneous',
  10: 'VAT Journal',
  99: 'Other',
};

export function mapInvoiceStatus(status: number): string {
  return INVOICE_STATUS_MAP[status] || 'Unknown';
}

/** Derive the display status for an invoice, promoting Sent/PartiallyPaid to
 *  Overdue when the due date has passed. */
export function deriveInvoiceDisplayStatus(inv: BillingInvoice): string {
  const base = mapInvoiceStatus(inv.status);
  if ((base === 'Sent' || base === 'PartiallyPaid') && inv.dueDate) {
    const due = new Date(inv.dueDate);
    due.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (due < today) return 'Overdue';
  }
  return base;
}

export function mapWipSourceType(sourceType: number): string {
  return WIP_SOURCE_TYPE_MAP[sourceType] || 'Unknown';
}

export interface WipSummary {
  totalNetAmount: number;
  timeNetAmount: number;
  expenseNetAmount: number;
  timeHours: number;
  writeUpTotal?: number;
}

export interface PaginatedBillingWipResponse {
  items: BillingWipItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  summary?: WipSummary;
}

export async function fetchBillingWipItems(projectId: number, status?: number, page?: number, pageSize?: number): Promise<BillingWipItem[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    // Demo mode returns empty array - mock data is handled in the component
    return [];
  } else {
    const statusParam = status !== undefined ? `?status=${status}` : '';
    return apiFetch<BillingWipItem[]>(`${getApiBaseUrl()}/billing/${projectId}/wip${statusParam}`);
  }
}

/** Serialises the optional server-side search/filter/sort state onto the WIP query string. */
export function buildWipQueryString(
  status: number,
  page: number,
  pageSize: number,
  options?: WipQueryOptions
): string {
  const params = new URLSearchParams();
  params.set('status', String(status));
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  if (options?.search) params.set('search', options.search);
  if (options?.sortBy) {
    params.set('sortBy', options.sortBy);
    if (options.sortDirection) params.set('sortDirection', options.sortDirection);
  }
  // Multi-value filters go as repeated parameters (ASP.NET Core array binding).
  options?.sourceTypes?.forEach((sourceType) => params.append('sourceTypes', String(sourceType)));
  options?.resources?.forEach((resource) => params.append('resources', resource));
  if (options?.dateFrom) params.set('dateFrom', options.dateFrom);
  if (options?.dateTo) params.set('dateTo', options.dateTo);

  return params.toString();
}

export interface WipFilterOptionsResponse {
  resources: string[];
  sourceTypes: number[];
  costCentres: string[];
  minDate: string | null;
  maxDate: string | null;
}

/**
 * Selectable filter values derived from all of the project's visible WIP, so the
 * filter sheet is not limited to whatever the current page happens to contain.
 */
export async function fetchBillingWipFilterOptions(projectId: number, status = 0): Promise<WipFilterOptionsResponse> {
  const mode = getDataMode();

  if (mode === 'demo') {
    return { resources: [], sourceTypes: [], costCentres: [], minDate: null, maxDate: null };
  }
  return apiFetch<WipFilterOptionsResponse>(
    `${getApiBaseUrl()}/billing/${projectId}/wip/filter-options?status=${status}`
  );
}

export async function fetchBillingWipItemsPaged(
  projectId: number,
  status: number,
  page: number,
  pageSize: number,
  options?: WipQueryOptions
): Promise<PaginatedBillingWipResponse> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    return { items: [], totalCount: 0, page, pageSize, summary: { totalNetAmount: 0, timeNetAmount: 0, expenseNetAmount: 0, timeHours: 0 } };
  } else {
    return apiFetch<PaginatedBillingWipResponse>(
      `${getApiBaseUrl()}/billing/${projectId}/wip?${buildWipQueryString(status, page, pageSize, options)}`
    );
  }
}

export async function fetchBillingInvoices(projectId: number, status?: number): Promise<BillingInvoice[]> {
  const mode = getDataMode();
  
  if (mode === 'demo') {
    return [];
  } else {
    const statusParam = status !== undefined ? `?status=${status}` : '';
    return apiFetch<BillingInvoice[]>(`${getApiBaseUrl()}/billing/${projectId}/invoices${statusParam}`);
  }
}

export interface PaginatedBillingInvoiceResponse {
  items: BillingInvoice[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalInvoiced: number;
  /** "Invoiced" excluding VAT, for like-for-like comparison against the net budget. */
  totalInvoicedNet: number;
  totalOutstanding: number;
  totalPaid: number;
}

export async function fetchBillingInvoicesPaged(
  projectId: number,
  page: number,
  pageSize: number,
  status?: number,
  search?: string
): Promise<PaginatedBillingInvoiceResponse> {
  const mode = getDataMode();

  if (mode === 'demo') {
    return { items: [], totalCount: 0, page, pageSize, totalInvoiced: 0, totalInvoicedNet: 0, totalOutstanding: 0, totalPaid: 0 };
  } else {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (status !== undefined) params.set('status', String(status));
    if (search) params.set('search', search);
    return apiFetch<PaginatedBillingInvoiceResponse>(
      `${getApiBaseUrl()}/billing/${projectId}/invoices?${params.toString()}`
    );
  }
}

/** Matches the `TransactionType` vocabulary in transaction-filter-sheet.tsx. */
export type BillingTransactionType = 'Invoice' | 'CreditNote';

export interface BillingTransaction {
  transactionType: BillingTransactionType;
  id: number;
  projectId: number;
  number: string | null;
  date: string | null;
  dueDate: string | null;
  /** Invoices: 0..8. Credit notes: 0=Draft, 1=Posted. */
  status: number;
  statusText: string;
  subtotal: number;
  taxAmount: number;
  grossTotal: number;
}

export interface PaginatedBillingTransactionResponse {
  items: BillingTransaction[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export async function fetchBillingTransactionsPaged(
  page: number,
  pageSize: number,
  projectId?: number,
  status?: number,
  type?: BillingTransactionType,
  search?: string
): Promise<PaginatedBillingTransactionResponse> {
  const mode = getDataMode();

  if (mode === 'demo') {
    return { items: [], totalCount: 0, page, pageSize };
  } else {
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (projectId !== undefined) params.set('projectId', String(projectId));
    if (status !== undefined) params.set('status', String(status));
    if (type) params.set('type', type);
    if (search) params.set('search', search);
    return apiFetch<PaginatedBillingTransactionResponse>(
      `${getApiBaseUrl()}/billing/transactions?${params.toString()}`
    );
  }
}

export async function refreshInvoicePaymentStatus(): Promise<{ updatedCount: number; message: string }> {
  return apiFetch<{ updatedCount: number; message: string }>(
    `${getApiBaseUrl()}/billing/invoices/refresh-payment-status`,
    { method: 'POST' }
  );
}

export async function fetchBillingInvoiceById(invoiceId: number): Promise<{ invoice: BillingInvoice; lineItems: BillingInvoiceLineItem[] }> {
  const mode = getDataMode();

  if (mode === 'demo') {
    // Find the matching demo invoice, or fall back to a sensible default draft
    const demoInv = DEMO_INVOICES.find((i) => i.id === invoiceId) ?? DEMO_INVOICES[4]; // index 4 = id:5 Draft
    const statusMap: Record<string, number> = {
      Draft: 0, Sent: 1, PartiallyPaid: 2, Paid: 3, Overdue: 4, Void: 5, WriteOff: 6, Credited: 7, PartiallyCredited: 8,
    };
    const invoice: BillingInvoice = {
      id: demoInv.id,
      projectId: 1,
      invoiceNumber: demoInv.invoiceNumber ?? undefined,
      invoiceDate: demoInv.date ?? undefined,
      dueDate: demoInv.due ?? undefined,
      description: demoInv.description,
      status: statusMap[demoInv.status] ?? 0,
      subtotal: demoInv.net,
      taxRate: 20,
      taxAmount: demoInv.vat,
      grossTotal: demoInv.amount,
    };
    const lineItems: BillingInvoiceLineItem[] = [
      {
        id: 1,
        invoiceId: demoInv.id,
        sortOrder: 1,
        description: 'Development Services',
        quantity: 10,
        rate: demoInv.net / 10,
        netAmount: demoInv.net,
        taxAmount: demoInv.vat,
        grossAmount: demoInv.amount,
        type: 'Time',
      },
    ];
    return { invoice, lineItems };
  }

  return apiFetch(`${getApiBaseUrl()}/billing/invoices/${invoiceId}`);
}

export interface CustomerTermsResult {
  dueDays: number | null;
  dueType?: string;
  code?: string;
  description?: string;
  error?: string;
}

export async function getCustomerTerms(customerCode: string, companyId: number): Promise<CustomerTermsResult> {
  return apiFetch<CustomerTermsResult>(`${getApiBaseUrl()}/billing/customer-terms?customerCode=${encodeURIComponent(customerCode)}&companyId=${companyId}`);
}

export interface CustomerTaxRateResult {
  vatCode: string | null;
  vatRate: number | null;
}

export async function getCustomerTaxRate(customerCode: string, companyId: number): Promise<CustomerTaxRateResult> {
  return apiFetch<CustomerTaxRateResult>(`${getApiBaseUrl()}/billing/customer-tax-rate?customerCode=${encodeURIComponent(customerCode)}&companyId=${companyId}`);
}

export async function createBillingDraft(projectId: number, wipItemIds: number[], description?: string, notes?: string, invoiceDate?: string, dueDate?: string, options?: { isFixedFee?: boolean; fixedFeeAmount?: number; costCentreCode?: string; milestoneId?: number; manualInvoiceNumber?: string; hasAgreedValue?: boolean; agreedValueWriteOffReason?: string }): Promise<BillingInvoice> {
  return apiFetch<BillingInvoice>(`${getApiBaseUrl()}/billing/${projectId}/drafts`, {
    method: 'POST',
    body: JSON.stringify({ wipItemIds, description, notes, invoiceDate, dueDate, ...options }),
  });
}

export interface UpdateDraftRequest {
  additions?: number[];
  removals?: number[];
  edits?: Array<{
    wipLedgerId: number;
    quantity?: number;
    rate?: number;
    netAmount?: number;
    description?: string;
  }>;
}

export async function updateBillingDraft(invoiceId: number, updates: UpdateDraftRequest): Promise<BillingInvoice> {
  return apiFetch<BillingInvoice>(`${getApiBaseUrl()}/billing/drafts/${invoiceId}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  });
}

export async function updateDraftDescription(invoiceId: number, description: string | null): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}/description`, {
    method: 'PATCH',
    body: JSON.stringify({ description }),
  });
}

export async function deleteBillingDraft(invoiceId: number): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}`, {
    method: 'DELETE',
  });
}

export async function updateDraftEmailData(invoiceId: number, emailData: { to?: string; cc?: string; bcc?: string; subject?: string; body?: string; templateId?: string }): Promise<void> {
  await apiFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}/email-draft`, {
    method: 'PATCH',
    body: JSON.stringify(emailData),
  });
}

export interface PostToFinancialsResponse {
  success: boolean;
  message?: string;
  financialsIds?: number[];
  invoiceNumber?: string;
  pdfUrl?: string;
  error?: string;
  validationErrors?: Array<{
    message?: string;
    details?: Array<{ primaryKey?: string; field?: string; value?: string }>;
  }>;
}

export async function postBillingDraftToFinancials(invoiceId: number): Promise<PostToFinancialsResponse> {
  console.log(`[Billing] Posting draft invoice ${invoiceId} to Financials...`);

  const response = await authFetch(`${getApiBaseUrl()}/billing/drafts/${invoiceId}/post`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();
  console.log(`[Billing] Response status: ${response.status}`, data);

  if (response.ok) {
    // 200 OK — success
    console.log(`[Billing] Success — Financials IDs:`, data.financialsIds);
    return {
      success: true,
      message: data.message,
      financialsIds: data.financialsIds,
    };
  }

  if (response.status === 422) {
    // Validation errors from Financials API
    console.warn(`[Billing] Validation errors for invoice ${invoiceId}:`, data.validationErrors);
    return {
      success: false,
      error: data.error,
      validationErrors: data.validationErrors,
      financialsIds: data.financialsIds,
    };
  }

  if (response.status === 400) {
    // Bad request (e.g. invoice not a draft)
    console.warn(`[Billing] Bad request for invoice ${invoiceId}:`, data.error);
    return {
      success: false,
      error: data.error || 'Invalid request',
    };
  }

  // 500 / other errors (backend returns 500 when Financials API fails)
  console.error(`[Billing] Error posting invoice ${invoiceId} — status ${response.status}:`, data);
  return {
    success: false,
    error: data.error || data.message || `Request failed with status ${response.status}`,
    financialsIds: data.financialsIds,
  };
}

export async function finaliseBillingInvoice(invoiceId: number): Promise<PostToFinancialsResponse> {
  console.log(`[Billing] Finalising invoice ${invoiceId} without email...`);

  const response = await authFetch(`${getApiBaseUrl()}/billing/invoices/${invoiceId}/finalise`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  const data = await response.json();
  console.log(`[Billing] Response status: ${response.status}`, data);

  if (response.ok) {
    console.log(`[Billing] Success — Financials IDs:`, data.financialsIds);
    return {
      success: true,
      message: data.message,
      financialsIds: data.financialsIds,
      invoiceNumber: data.invoiceNumber,
      pdfUrl: data.pdfUrl,
    };
  }

  if (response.status === 422) {
    console.warn(`[Billing] Validation errors for invoice ${invoiceId}:`, data.validationErrors);
    return {
      success: false,
      error: data.error,
      validationErrors: data.validationErrors,
      financialsIds: data.financialsIds,
    };
  }

  if (response.status === 400) {
    console.warn(`[Billing] Bad request for invoice ${invoiceId}:`, data.error);
    return {
      success: false,
      error: data.error || 'Invalid request',
    };
  }

  console.error(`[Billing] Error finalising invoice ${invoiceId} — status ${response.status}:`, data);
  return {
    success: false,
    error: data.error || data.message || `Request failed with status ${response.status}`,
    financialsIds: data.financialsIds,
  };
}

export async function voidBillingInvoice(invoiceId: number, reason: string): Promise<BillingInvoice> {
  return apiFetch<BillingInvoice>(`${getApiBaseUrl()}/billing/invoices/${invoiceId}/void`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

// ── Company Billing Settings ───────────────────────────────────────────

export interface CompanyBillingSettingsResponse {
  autoNumberingEnabled: boolean;
  lastInvoiceNumber: string | null;
  agedWipDaysThreshold: number | null;
  agedWipAmountWarning: number | null;
  agedWipAmountAlert: number | null;
}

export async function getCompanyBillingSettings(): Promise<CompanyBillingSettingsResponse> {
  const raw = await apiFetch<Partial<CompanyBillingSettingsResponse>>(`${getApiBaseUrl()}/company/billing-settings`);
  return {
    autoNumberingEnabled: raw.autoNumberingEnabled ?? true,
    lastInvoiceNumber: raw.lastInvoiceNumber ?? null,
    agedWipDaysThreshold: raw.agedWipDaysThreshold ?? null,
    agedWipAmountWarning: raw.agedWipAmountWarning ?? null,
    agedWipAmountAlert: raw.agedWipAmountAlert ?? null,
  };
}

export async function patchCompanyBillingSettings(data: {
  autoNumberingEnabled?: boolean;
  agedWipDaysThreshold?: number;
  agedWipAmountWarning?: number;
  agedWipAmountAlert?: number;
}): Promise<CompanyBillingSettingsResponse> {
  const raw = await apiFetch<Partial<CompanyBillingSettingsResponse>>(`${getApiBaseUrl()}/company/billing-settings`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return {
    autoNumberingEnabled: raw.autoNumberingEnabled ?? true,
    lastInvoiceNumber: raw.lastInvoiceNumber ?? null,
    agedWipDaysThreshold: raw.agedWipDaysThreshold ?? null,
    agedWipAmountWarning: raw.agedWipAmountWarning ?? null,
    agedWipAmountAlert: raw.agedWipAmountAlert ?? null,
  };
}

export interface AccessFinancialsSettingsResponse {
  autoInvoiceNumbering: boolean;
}

export async function getAccessFinancialsSettings(): Promise<AccessFinancialsSettingsResponse> {
  return apiFetch<AccessFinancialsSettingsResponse>(`${getApiBaseUrl()}/integrations/access-financials/settings`);
}

// ── Invoice Number Validation ──────────────────────────────────────────

export interface ValidateInvoiceNumberResponse {
  isValid: boolean;
  isDuplicate: boolean;
  message?: string;
}

export async function validateInvoiceNumber(invoiceNumber: string): Promise<ValidateInvoiceNumberResponse> {
  return apiFetch<ValidateInvoiceNumberResponse>(`${getApiBaseUrl()}/billing/invoices/validate-number`, {
    method: 'POST',
    body: JSON.stringify({ invoiceNumber }),
  });
}

// ── Project Transactions ───────────────────────────────────────────────

export interface ProjectTransactionQueryParams {
  first?: number;
  after?: string | null;
  projectCode?: string;
  entryType?: string;
  isBatched?: boolean | null;
  isInvoiced?: boolean | null;
  dateFrom?: string;
  dateTo?: string;
  sortField?: string;
  sortOrder?: 'Ascending' | 'Descending';
}

export interface ProjectTransactionPageInfo {
  endCursor: string | null;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
}

export interface ProjectTransactionDto {
  transactionId: number | null;
  projectCode: string | null;
  projectName: string | null;
  projectStatus: string | null;
  date: string | null;
  periodNumber: number | null;
  yearLabel: string | null;
  weekNo: number | null;
  dateEntered: string | null;
  transactionType: string | null;
  transactionStatus: string | null;
  source: string | null;
  entryType: string | null;
  isBatched: boolean | null;
  isInvoiced: boolean | null;
  reference: string | null;
  internalRef: number | null;
  detail: string | null;
  batchRef: string | null;
  sortKey: string | null;
  headerKey: string | null;
  resourceCode: string | null;
  resourceName: string | null;
  resourcePriceLine: number | null;
  employeeCode: string | null;
  enteredBy: string | null;
  accountCode: string | null;
  accountName: string | null;
  invoiceAccountCode: string | null;
  invoiceAccountName: string | null;
  orderAccountCode: string | null;
  orderAccountName: string | null;
  supplierAccountCode: string | null;
  costCentreCode: string | null;
  costCentreName: string | null;
  quantity: number | null;
  price: number | null;
  value: number | null;
  uplift: number | null;
  upliftValue: number | null;
  priceWithUplift: number | null;
  currencyCode: string | null;
  currentPrice: number | null;
  currentValue: number | null;
  currentUpliftValue: number | null;
  actualSales: number | null;
  actualCost: number | null;
  salesOrderNumber: number | null;
  purchaseOrderNumber: string | null;
  stockCode: string | null;
  stockName: string | null;
  analysisCode: string | null;
  analysisName: string | null;
  nominalCode: string | null;
  payElement: string | null;
  payRate: number | null;
  subLedger: string | null;
  customField1: string | null;
  customField2: string | null;
  customField3: string | null;
  customField4: string | null;
  customField5: string | null;
  customField6: string | null;
  userKey1: string | null;
  userKey2: string | null;
  userKey3: string | null;
  userChar1: string | null;
  userChar2: string | null;
  userChar3: string | null;
  userChar4: string | null;
  userDate1: string | null;
  userDate2: string | null;
  userFlag1: boolean | null;
  userFlag2: boolean | null;
  userNumber1: number | null;
  userNumber2: number | null;
}

export interface ProjectTransactionResponse {
  items: ProjectTransactionDto[];
  pageInfo: ProjectTransactionPageInfo | null;
  errorMessage: string | null;
}

/**
 * Fetches project transactions from the backend.
 * In demo mode returns mock data; in live mode queries the real API
 * which delegates to the external transaction data provider (e.g. Financials GraphQL).
 */
export async function fetchProjectTransactions(
  params: ProjectTransactionQueryParams = {}
): Promise<ProjectTransactionResponse> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 400));
    // Return empty result for demo mode — the page already has its own mock data
    return { items: [], pageInfo: null, errorMessage: null };
  }

  // Build query string — companyId is sent via the X-Company-Id header by authFetch
  const queryParams = new URLSearchParams();
  if (params.first) queryParams.set('first', params.first.toString());
  if (params.after) queryParams.set('after', params.after);
  if (params.projectCode) queryParams.set('projectCode', params.projectCode);
  if (params.entryType) queryParams.set('entryType', params.entryType);
  if (params.isBatched != null) queryParams.set('isBatched', params.isBatched.toString());
  if (params.isInvoiced != null) queryParams.set('isInvoiced', params.isInvoiced.toString());
  if (params.dateFrom) queryParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) queryParams.set('dateTo', params.dateTo);
  if (params.sortField) queryParams.set('sortField', params.sortField);
  if (params.sortOrder) queryParams.set('sortOrder', params.sortOrder);

  return apiFetch<ProjectTransactionResponse>(
    `${getApiBaseUrl()}/projecttransactions?${queryParams.toString()}`
  );
}

/**
 * Request body for the POST /api/projecttransactions/query endpoint.
 * Supports multi-field sorting and structured filters for server-side
 * pagination, filtering, and sorting.
 */
export interface ProjectTransactionQueryBody {
  companyId?: string;
  first: number;
  after?: string | null;
  sort?: Array<{ field: string; order: string }>;
  projectCode?: string;
  projectCodes?: string[];
  transactionTypes?: string[];
  sources?: string[];
  entryType?: string;
  transactionStatusMode?: 'all' | 'actual' | 'estimate';
  isBatched?: boolean;
  isInvoiced?: boolean;
  projectStatuses?: string[];
  dateFrom?: string;
  dateTo?: string;
  searchTerm?: string;
  analysisCodes?: string[];
}

/**
 * Queries project transactions using the POST /query endpoint.
 * Supports multi-field sorting and advanced filters via a JSON body,
 * enabling true server-side pagination, filtering, and sorting.
 */
export async function queryProjectTransactions(
  body: ProjectTransactionQueryBody
): Promise<ProjectTransactionResponse> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 400));
    return { items: [], pageInfo: null, errorMessage: null };
  }

  return apiFetch<ProjectTransactionResponse>(
    `${getApiBaseUrl()}/projecttransactions/query`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    }
  );
}

// ── Transaction Totals (server-side KPIs) ──────────────────────────────

export interface ProjectTransactionTotalsResponse {
  totalCount: number;
  totalSales: number;
  totalCost: number;
  netTotal: number;
  errorMessage: string | null;
}

export async function queryProjectTransactionTotals(
  body: ProjectTransactionQueryBody
): Promise<ProjectTransactionTotalsResponse> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return { totalCount: 0, totalSales: 0, totalCost: 0, netTotal: 0, errorMessage: null };
  }
  return apiFetch<ProjectTransactionTotalsResponse>(
    `${getApiBaseUrl()}/projecttransactions/totals`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// ── Transaction Grouped Totals (server-side Group By) ──────────────────

export interface ProjectTransactionGroupRow {
  key: string;
  count: number;
  totalSales: number;
  totalCost: number;
  net: number;
}

export interface ProjectTransactionGroupedTotalsResponse {
  groups: ProjectTransactionGroupRow[];
  errorMessage: string | null;
}

export async function queryProjectTransactionGroupedTotals(
  body: ProjectTransactionQueryBody & { groupBy: string }
): Promise<ProjectTransactionGroupedTotalsResponse> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return { groups: [], errorMessage: null };
  }
  return apiFetch<ProjectTransactionGroupedTotalsResponse>(
    `${getApiBaseUrl()}/projecttransactions/grouped-totals`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// ── Sales Transactions ─────────────────────────────────────────────────

export interface BulkCreateSalesTransactionRequest {
  transactions: Array<Record<string, unknown>>;
  validateOnly?: boolean;
}

export interface BulkCreateSalesTransactionResponse {
  ids?: number[];
  errors?: Array<{ message?: string; details?: Array<{ primaryKey?: string; field?: string; value?: string }> }>;
  warnings?: Array<{ message?: string; details?: Array<{ primaryKey?: string; field?: string; value?: string }> }>;
  errorMessage?: string;
}

export async function bulkCreateSalesTransactions(
  request: BulkCreateSalesTransactionRequest,
  companyId: number
): Promise<BulkCreateSalesTransactionResponse> {
  return apiFetch<BulkCreateSalesTransactionResponse>(
    `${getApiBaseUrl()}/billing/sales-transactions/bulk-create?companyId=${companyId}`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
}

// ── Revision Reason Categories ───────────────────────────────────────────

export async function fetchRevisionReasonCategories(): Promise<RevisionReasonCategoryConfig[]> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<RevisionReasonCategoryConfig[]>(`${baseUrl}/RevisionReasonCategories`);
}

export async function createRevisionReasonCategory(data: { label: string; displayOrder: number }): Promise<RevisionReasonCategoryConfig> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<RevisionReasonCategoryConfig>(`${baseUrl}/RevisionReasonCategories`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function updateRevisionReasonCategory(id: number, data: { label?: string; isEnabled?: boolean; displayOrder?: number }): Promise<void> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<void>(`${baseUrl}/RevisionReasonCategories/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function deleteRevisionReasonCategory(id: number): Promise<void> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<void>(`${baseUrl}/RevisionReasonCategories/${id}`, {
    method: 'DELETE',
  });
}

export async function reorderRevisionReasonCategories(updates: { id: number; displayOrder: number }[]): Promise<void> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<void>(`${baseUrl}/RevisionReasonCategories/reorder`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ updates }),
  });
}

// ── Category Types ──────────────────────────────────────────────────────────

const DEMO_CATEGORY_TYPES: CategoryType[] = [
  { id: 1, code: 'labour',         label: 'Labour',         abbreviation: 'LAB', description: 'Labour and professional services',    displayOrder: 1, isSystem: true, isActive: true },
  { id: 2, code: 'expenses',       label: 'Expenses',       abbreviation: 'EXP', description: 'Business expenses',                   displayOrder: 2, isSystem: true, isActive: true },
  { id: 3, code: 'subcontractors', label: 'Subcontractors', abbreviation: 'SUB', description: 'External subcontractor costs',        displayOrder: 3, isSystem: true, isActive: true },
  { id: 4, code: 'materials',      label: 'Materials',      abbreviation: 'MAT', description: 'Materials and supplies',               displayOrder: 4, isSystem: true, isActive: true },
  { id: 5, code: 'equipment',      label: 'Equipment',      abbreviation: 'EQP', description: 'Equipment costs',                     displayOrder: 5, isSystem: true, isActive: true },
  { id: 6, code: 'overhead',       label: 'Overhead',       abbreviation: 'OVH', description: 'Overhead and indirect costs',         displayOrder: 6, isSystem: true, isActive: true },
  { id: 7, code: 'contingency',    label: 'Contingency',    abbreviation: 'CTG', description: 'Contingency and risk reserves',       displayOrder: 7, isSystem: true, isActive: true },
];

export async function fetchCategoryTypes(): Promise<CategoryType[]> {
  const mode = getDataMode();

  if (mode === 'demo') {
    await new Promise(resolve => setTimeout(resolve, 200));
    return DEMO_CATEGORY_TYPES;
  } else {
    return apiFetch<CategoryType[]>(`${getApiBaseUrl()}/category-types`);
  }
}

// ── Write-Off API functions ─────────────────────────────────────────────────

export interface WriteOffReason {
  id: number;
  code: string;
  label: string;
  isDefault: boolean;
  isActive: boolean;
  sortOrder: number;
}

export async function writeOffWipItems(wipItemIds: number[], reasonCode?: string): Promise<{ message: string; count: number }> {
  return apiFetch<{ message: string; count: number }>(`${getApiBaseUrl()}/billing/wip/write-off`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ wipItemIds, reasonCode }),
  });
}

// ── Credit Note API functions ───────────────────────────────────────────────

export interface CreateCreditNoteParams {
  creditType: 'full' | 'partial';
  creditAmount?: number;
  tamDisposition?: 'return-to-wip' | 'write-off';
  writeOffReasonCode?: string;
}

export interface CreditNoteResponse {
  id: number;
  invoiceId: number;
  projectId: number;
  creditNoteNumber: string | null;
  creditType: string;
  creditAmount: number;
  tamDisposition: string | null;
  writeOffReasonCode: string | null;
  status: number;
  accessFinancialsId: string | null;
  subtotal: number;
  taxAmount: number;
  grossTotal: number;
  postedAt: string | null;
  postedBy: string | null;
  createdBy: string | null;
}

export async function createCreditNote(invoiceId: number, params: CreateCreditNoteParams): Promise<CreditNoteResponse> {
  return apiFetch<CreditNoteResponse>(`${getApiBaseUrl()}/billing/invoices/${invoiceId}/credit-note`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      creditType: params.creditType,
      creditAmount: params.creditAmount,
      tamDisposition: params.tamDisposition,
      writeOffReasonCode: params.writeOffReasonCode,
    }),
  });
}

export async function undoWriteOff(wipItemId: number): Promise<{ message: string }> {
  return apiFetch<{ message: string }>(`${getApiBaseUrl()}/billing/wip/${wipItemId}/undo-write-off`, {
    method: 'POST',
  });
}

export async function fetchWriteOffReasons(): Promise<WriteOffReason[]> {
  // In demo/tour mode there is no backend — return an empty list so callers
  // can merge in their own DEMO_DEFAULTS without hitting a network error.
  if (getDataMode() !== 'live') return [];
  return apiFetch<WriteOffReason[]>(`${getApiBaseUrl()}/settings/write-off-reasons`);
}

export interface BillingSettings {
  /** Legacy field — still returned/accepted by the backend */
  requireWriteOffReason: boolean;
  /** New field: require a reason when writing DOWN WIP. Defaults from requireWriteOffReason if absent. */
  requireWriteDownReason: boolean;
  /** New field: require a reason when writing UP WIP. Defaults to false if absent. */
  requireWriteUpReason: boolean;
}

const WRITE_UP_REASON_KEY = 'eb_requireWriteUpReason';
const WRITE_DOWN_REASON_KEY = 'eb_requireWriteDownReason';

export async function fetchBillingSettings(): Promise<BillingSettings> {
  // In demo/tour mode there is no backend — return defaults from localStorage only.
  if (getDataMode() !== 'live') {
    const storedWriteUp = localStorage.getItem(WRITE_UP_REASON_KEY);
    const requireWriteUpReason = storedWriteUp !== null ? storedWriteUp === 'true' : false;
    const storedWriteDown = localStorage.getItem(WRITE_DOWN_REASON_KEY);
    const requireWriteDownReason = storedWriteDown !== null ? storedWriteDown === 'true' : false;
    return {
      requireWriteOffReason: requireWriteDownReason,
      requireWriteDownReason,
      requireWriteUpReason,
    };
  }

  const raw = await apiFetch<{ requireWriteOffReason?: boolean; requireWriteUpReason?: boolean }>(`${getApiBaseUrl()}/settings/billing`);
  const legacyFlag = raw.requireWriteOffReason ?? false;
  return {
    requireWriteOffReason: legacyFlag,
    requireWriteDownReason: legacyFlag,  // write-down maps 1:1 to the backend flag
    requireWriteUpReason: raw.requireWriteUpReason ?? false,
  };
}

export async function updateBillingSettings(settings: Partial<BillingSettings>): Promise<void> {
  // In demo/tour mode there is no backend — localStorage is the only store.
  if (getDataMode() !== 'live') {
    if ('requireWriteUpReason' in settings && settings.requireWriteUpReason !== undefined) {
      localStorage.setItem(WRITE_UP_REASON_KEY, String(settings.requireWriteUpReason));
    }
    if ('requireWriteDownReason' in settings && settings.requireWriteDownReason !== undefined) {
      localStorage.setItem(WRITE_DOWN_REASON_KEY, String(settings.requireWriteDownReason));
    }
    return;
  }

  // requireWriteDownReason maps to the backend's requireWriteOffReason.
  const backendPayload: { requireWriteOffReason?: boolean; requireWriteUpReason?: boolean } = {};
  if ('requireWriteOffReason' in settings && settings.requireWriteOffReason !== undefined) {
    backendPayload.requireWriteOffReason = settings.requireWriteOffReason;
  }
  if ('requireWriteDownReason' in settings && settings.requireWriteDownReason !== undefined) {
    backendPayload.requireWriteOffReason = settings.requireWriteDownReason;
  }
  if ('requireWriteUpReason' in settings && settings.requireWriteUpReason !== undefined) {
    backendPayload.requireWriteUpReason = settings.requireWriteUpReason;
  }

  if (Object.keys(backendPayload).length > 0) {
    await apiFetch<void>(`${getApiBaseUrl()}/settings/billing`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(backendPayload),
    });
  }
}

export async function createWriteOffReason(reason: { code: string; label: string; isDefault?: boolean; sortOrder?: number }): Promise<WriteOffReason> {
  return apiFetch<WriteOffReason>(`${getApiBaseUrl()}/settings/write-off-reasons`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason),
  });
}

export async function updateWriteOffReason(id: number, reason: { code: string; label: string; isDefault?: boolean; sortOrder?: number }): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/settings/write-off-reasons/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(reason),
  });
}

export async function deleteWriteOffReason(id: number): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/settings/write-off-reasons/${id}`, {
    method: 'DELETE',
  });
}

// ── Project Template API functions ──────────────────────────────────────────

function parseJsonFields(template: Record<string, unknown>): ProjectTemplate {
  const parsed = { ...template } as Record<string, unknown>;
  if (typeof parsed.costCentres === 'string') {
    try { parsed.costCentres = JSON.parse(parsed.costCentres as string); } catch { parsed.costCentres = []; }
  }
  parsed.costCentres = parsed.costCentres ?? [];
  if (typeof parsed.resourceBudget === 'string') {
    try { parsed.resourceBudget = JSON.parse(parsed.resourceBudget as string); } catch { parsed.resourceBudget = []; }
  }
  parsed.resourceBudget = parsed.resourceBudget ?? [];
  // Map backend numeric id to string for frontend compatibility
  if (parsed.id !== undefined && parsed.id !== null) {
    parsed.id = String(parsed.id);
  }
  return parsed as unknown as ProjectTemplate;
}

function stringifyJsonFields(data: Record<string, unknown>): Record<string, unknown> {
  const payload = { ...data };
  if (payload.costCentres !== undefined && typeof payload.costCentres !== 'string') {
    payload.costCentres = JSON.stringify(payload.costCentres ?? []);
  }
  if (payload.resourceBudget !== undefined && typeof payload.resourceBudget !== 'string') {
    payload.resourceBudget = JSON.stringify(payload.resourceBudget ?? []);
  }
  return payload;
}

export async function fetchProjectTemplates(): Promise<ProjectTemplate[]> {
  const mode = getDataMode();
  if (mode === 'demo' || mode === 'tour') {
    return [];
  }
  const raw = await apiFetch<Record<string, unknown>[]>(`${getApiBaseUrl()}/project-templates`);
  return raw.map(parseJsonFields);
}

export async function fetchProjectTemplateById(id: string | number): Promise<ProjectTemplate> {
  const raw = await apiFetch<Record<string, unknown>>(`${getApiBaseUrl()}/project-templates/${id}`);
  return parseJsonFields(raw);
}

export async function createProjectTemplate(data: CreateTemplateData): Promise<ProjectTemplate> {
  const payload = stringifyJsonFields(data as unknown as Record<string, unknown>);
  const raw = await apiFetch<Record<string, unknown>>(`${getApiBaseUrl()}/project-templates`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return parseJsonFields(raw);
}

export async function updateProjectTemplate(id: string | number, data: Partial<CreateTemplateData> & { id?: string | number }): Promise<void> {
  const payload = stringifyJsonFields({ ...data, id: Number(id) } as unknown as Record<string, unknown>);
  await apiFetch<void>(`${getApiBaseUrl()}/project-templates/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function deleteProjectTemplate(id: string | number): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/project-templates/${id}`, {
    method: 'DELETE',
  });
}

export async function incrementProjectTemplateUsage(id: string | number): Promise<void> {
  await apiFetch<void>(`${getApiBaseUrl()}/project-templates/${id}/use`, {
    method: 'POST',
  });
}

// ── Profitability Analysis Report ────────────────────────────────────────────

/**
 * Fetches per-project profitability data for the Profitability Analysis Report.
 *
 * Demo mode: builds from `mockProjects` + `getMockBudgetData()`. Derives health
 * using the configured `BudgetHealthThresholds` so RAG colouring is consistent
 * with the rest of the application.
 *
 * Live mode: calls `GET /api/reports/profitability` and returns the `projects`
 * array from the response. The endpoint returns revenue/cost actuals sourced
 * from costing transactions and overhead from overhead-type cost categories.
 *
 * @returns Array of `ProjectProfitabilityData`, one entry per project. Projects
 *          without budget data (no `allBudgets.profitability`) are excluded so
 *          the table never renders rows with undefined financial figures.
 */
export async function fetchProfitabilityData(): Promise<import('@/types/reports.types').ProjectProfitabilityData[]> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 350));

    const projects = mode === 'demo' ? mockData.mockProjects : (tourMockData as any).tourMockProjects ?? mockData.mockProjects;
    const thresholds = getBudgetHealthThresholds();

    const results = await Promise.all(
      projects.map(async (project: any) => {
        const budgetData = getMockBudgetData(project.id);
        const profitability = budgetData?.allBudgets?.profitability;

        // Skip projects with no profitability data (should not happen in demo — all
        // projects have either explicit or dynamically-generated profitability)
        if (!profitability) return null;

        // Derive health consistently with the project list
        const health = deriveProjectHealth(
          {
            actualCost: (project.actualLabour ?? 0) + (project.actualExpense ?? 0),
            budgetCost: (project.budgetLabour ?? 0) + (project.budgetExpense ?? 0),
            startDate: project.startDate,
            endDate: project.endDate,
            completionPercent: project.completionPercentOverride ?? project.completionPercent ?? 0,
            actualHours: project.actualHours ?? 0,
            budgetHours: project.budgetHours ?? 0,
            wipAmount: project.wipAmount ?? 0,
            actualRevenue: project.actualRevenue ?? 0,
            budgetRevenue: project.budgetRevenue ?? 0,
            forecastEndDate: project.forecastEndDate,
          },
          thresholds
        );

        // Resolve PM name from roleAssignments (preferred) or fall back to
        // projectManagerId lookup via mockEmployees
        const pmFromRoles = project.roleAssignments?.find(
          (r: any) => r.role === 'project-manager'
        )?.employeeName;
        const pmFromId = pmFromRoles ?? (
          mockData.mockEmployees?.find((e: any) => e.id === project.projectManagerId)?.name ?? '—'
        );

        const entry: import('@/types/reports.types').ProjectProfitabilityData = {
          projectId: project.id,
          projectName: project.name,
          projectCode: project.code ?? '',
          clientName: project.clientNames ?? '—',
          projectManager: pmFromId,
          projectType: project.type ?? '',
          status: project.status ?? String(project.statusId ?? ''),
          health,
          billable: project.billable ?? true,
          profitability,
        };

        return entry;
      })
    );

    // Filter out any null entries (projects without profitability data)
    return results.filter((r): r is import('@/types/reports.types').ProjectProfitabilityData => r !== null);
  }

  // Live mode — calls GET /api/reports/profitability (§8.5)
  const baseUrl = getApiBaseUrl();
  const response = await apiFetch<{ projects: import('@/types/reports.types').ProjectProfitabilityData[] }>(
    `${baseUrl}/reports/profitability`,
    { credentials: 'include' }
  );
  return response.projects;
}

/** Parameters for the paginated profitability report endpoint. */
export interface ProfitabilityReportParams {
  page?: number;
  pageSize?: number;
  status?: string;
  pm?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** 'billable' | 'non-billable' — omit for all */
  billable?: string;
}

/**
 * Fetches a single page of profitability data from the server.
 *
 * Demo mode: falls back to the full client-side dataset wrapped in a paginated
 * response shape so the page component can use the same code path.
 *
 * Live mode: calls `GET /api/reports/profitability?page=&pageSize=&…` which
 * returns only the requested page plus portfolio-level summary and filter options.
 */
export async function fetchProfitabilityDataPaged(
  params: ProfitabilityReportParams = {},
): Promise<import('@/types/reports.types').ProfitabilityPaginatedResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    // Re-use the existing demo builder then paginate client-side
    const allProjects = await fetchProfitabilityData();
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 25;

    // Apply filters
    let filtered = allProjects;
    if (params.status && params.status !== 'all')
      filtered = filtered.filter(d => d.status === params.status);
    if (params.pm && params.pm !== 'all')
      filtered = filtered.filter(d => d.projectManager === params.pm);
    if (params.search) {
      const searchLower = params.search.toLowerCase();
      filtered = filtered.filter(d =>
        d.projectName.toLowerCase().includes(searchLower) ||
        (d.projectCode && d.projectCode.toLowerCase().includes(searchLower))
      );
    }
    if (params.billable === 'billable') {
      filtered = filtered.filter(d => d.billable !== false);
    } else if (params.billable === 'non-billable') {
      filtered = filtered.filter(d => d.billable === false);
    }

    // Sort
    const sortKey = params.sortBy ?? 'grossMarginActual';
    const asc = params.sortOrder === 'asc';
    filtered = [...filtered].sort((a, b) => {
      let diff = 0;
      switch (sortKey) {
        case 'projectName':
          diff = a.projectName.localeCompare(b.projectName);
          break;
        case 'revenueActual':
          diff = a.profitability.revenue.actual - b.profitability.revenue.actual;
          break;
        case 'grossProfitActual':
          diff = a.profitability.grossProfit.actual - b.profitability.grossProfit.actual;
          break;
        default:
          diff = a.profitability.grossMargin.actual - b.profitability.grossMargin.actual;
      }
      return asc ? diff : -diff;
    });

    // Summary
    const totalGP = filtered.reduce((s, d) => s + d.profitability.grossProfit.actual, 0);
    const totalRev = filtered.reduce((s, d) => s + d.profitability.revenue.actual, 0);
    const thresholds = getBudgetHealthThresholds();

    const highest = filtered.length > 0
      ? filtered.reduce((best, d) => d.profitability.grossMargin.actual > best.profitability.grossMargin.actual ? d : best)
      : null;
    const lowest = filtered.length > 0
      ? filtered.reduce((worst, d) => d.profitability.grossMargin.actual < worst.profitability.grossMargin.actual ? d : worst)
      : null;

    // Paginate
    const { paginateArray } = await import('@/types/pagination');
    const { data, pagination } = paginateArray(filtered, page, pageSize);

    return {
      projects: data,
      portfolioSummary: {
        totalProjects: filtered.length,
        totalBudgetRevenue: filtered.reduce((s, d) => s + d.profitability.revenue.budget, 0),
        totalActualRevenue: totalRev,
        totalBudgetGP: filtered.reduce((s, d) => s + d.profitability.grossProfit.budget, 0),
        totalActualGP: totalGP,
        portfolioGPPercent: totalRev > 0 ? (totalGP / totalRev) * 100 : 0,
        projectsBelowAmberThreshold: filtered.filter(d => d.profitability.grossMargin.actual < (thresholds.marginAmberThreshold ?? 25)).length,
        totalOverheadBudget: filtered.reduce((s, d) => s + d.profitability.overhead.budget, 0),
        totalOverheadActual: filtered.reduce((s, d) => s + d.profitability.overhead.actual, 0),
        totalNetProfit: filtered.reduce((s, d) => s + d.profitability.netProfit.actual, 0),
        portfolioNetMarginPercent: 0,
        highestMarginProjectName: highest?.projectName ?? null,
        highestMarginValue: highest?.profitability.grossMargin.actual ?? 0,
        lowestMarginProjectName: lowest?.projectName ?? null,
        lowestMarginValue: lowest?.profitability.grossMargin.actual ?? 0,
      },
      pagination,
      filterOptions: {
        projectManagers: [...new Set(allProjects.map(d => d.projectManager).filter(Boolean))].sort(),
        statuses: [...new Set(allProjects.map(d => d.status).filter(Boolean))].sort(),
      },
    };
  }

  // Live mode — server-side pagination
  const baseUrl = getApiBaseUrl();
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);
  if (params.pm && params.pm !== 'all') searchParams.set('pm', params.pm);
  if (params.search) searchParams.set('search', params.search);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

  const qs = searchParams.toString();
  const url = `${baseUrl}/reports/profitability${qs ? `?${qs}` : ''}`;

  return apiFetch<import('@/types/reports.types').ProfitabilityPaginatedResponse>(url, {
    credentials: 'include',
  });
}

// ── Project P&L Report ──────────────────────────────────────────────────────

/**
 * Fetches Project P&L data for a single project.
 *
 * Demo mode: builds from `mockProjects` using lightweight `computeProjectCostBudget`
 * arithmetic (no expensive `getBudgetDataByProjectId` call). GP is calculated
 * client-side using `calculateGP()` and `calculateBudgetGP()`.
 *
 * Live mode: calls `GET /api/reports/project-pl/{projectId}` and returns the
 * response directly. The endpoint provides raw revenue and cost figures; GP is
 * still calculated client-side by the page component using the user's chosen method.
 */
export async function fetchProjectPLData(projectId: number): Promise<import('@/types/reports.types').ProjectPLReport | null> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 250));

    const projects = mode === 'demo' ? mockData.mockProjects : (tourMockData as any).tourMockProjects ?? mockData.mockProjects;
    const project = projects.find((p: any) => p.id === projectId);
    if (!project) return null;

    // Lightweight cost budget computation (same logic as computeProjectCostBudget in project-pl-report.tsx)
    const labourBudget  = project.budgetLabour ?? 0;
    const labourActual  = project.actualLabour ?? 0;
    const expenseBudget = project.budgetExpense ?? 0;
    const expenseActual = project.actualExpense ?? 0;

    const totalBudgetCost = labourBudget + expenseBudget;
    const totalActualCost = labourActual + expenseActual;

    // Category breakdown using fixed percentage splits
    const categories: import('@/types/reports.types').PLCostCategory[] = [
      { id: 1, name: 'Labour', type: 'labour', budget: labourBudget, actual: labourActual, variance: labourActual - labourBudget, variancePercent: labourBudget !== 0 ? ((labourActual - labourBudget) / labourBudget) * 100 : 0 },
      { id: 2, name: 'Materials', type: 'expense', budget: expenseBudget * 0.25, actual: expenseActual * 0.24, variance: (expenseActual * 0.24) - (expenseBudget * 0.25), variancePercent: (expenseBudget * 0.25) !== 0 ? (((expenseActual * 0.24) - (expenseBudget * 0.25)) / (expenseBudget * 0.25)) * 100 : 0 },
      { id: 3, name: 'Equipment', type: 'expense', budget: expenseBudget * 0.20, actual: expenseActual * 0.21, variance: (expenseActual * 0.21) - (expenseBudget * 0.20), variancePercent: (expenseBudget * 0.20) !== 0 ? (((expenseActual * 0.21) - (expenseBudget * 0.20)) / (expenseBudget * 0.20)) * 100 : 0 },
      { id: 4, name: 'Subcontractors', type: 'subcontractor', budget: expenseBudget * 0.15, actual: expenseActual * 0.16, variance: (expenseActual * 0.16) - (expenseBudget * 0.15), variancePercent: (expenseBudget * 0.15) !== 0 ? (((expenseActual * 0.16) - (expenseBudget * 0.15)) / (expenseBudget * 0.15)) * 100 : 0 },
      { id: 5, name: 'Travel & Expenses', type: 'expense', budget: expenseBudget * 0.15, actual: expenseActual * 0.14, variance: (expenseActual * 0.14) - (expenseBudget * 0.15), variancePercent: (expenseBudget * 0.15) !== 0 ? (((expenseActual * 0.14) - (expenseBudget * 0.15)) / (expenseBudget * 0.15)) * 100 : 0 },
      { id: 6, name: 'Software & Licenses', type: 'expense', budget: expenseBudget * 0.15, actual: expenseActual * 0.15, variance: (expenseActual * 0.15) - (expenseBudget * 0.15), variancePercent: (expenseBudget * 0.15) !== 0 ? (((expenseActual * 0.15) - (expenseBudget * 0.15)) / (expenseBudget * 0.15)) * 100 : 0 },
      { id: 7, name: 'Overhead', type: 'overhead', budget: expenseBudget * 0.07, actual: expenseActual * 0.07, variance: (expenseActual * 0.07) - (expenseBudget * 0.07), variancePercent: (expenseBudget * 0.07) !== 0 ? (((expenseActual * 0.07) - (expenseBudget * 0.07)) / (expenseBudget * 0.07)) * 100 : 0 },
      { id: 8, name: 'Contingency', type: 'expense', budget: expenseBudget * 0.03, actual: expenseActual * 0.03, variance: (expenseActual * 0.03) - (expenseBudget * 0.03), variancePercent: (expenseBudget * 0.03) !== 0 ? (((expenseActual * 0.03) - (expenseBudget * 0.03)) / (expenseBudget * 0.03)) * 100 : 0 },
    ];

    // Revenue
    const contractValue = project.budgetRevenue ?? 0;
    const invoicedRevenue = project.actualSales ?? project.actualRevenue ?? 0;
    const revenueVariance = invoicedRevenue - contractValue;
    const revenueVariancePercent = contractValue !== 0 ? (revenueVariance / contractValue) * 100 : 0;

    // Cost variance
    const costVariance = totalActualCost - totalBudgetCost;
    const costVariancePercent = totalBudgetCost !== 0 ? (costVariance / totalBudgetCost) * 100 : 0;

    // Hours
    const budgetHours = project.budgetHours ?? 0;
    const actualHours = project.actualHours ?? 0;
    const hoursVariance = actualHours - budgetHours;
    const hoursVariancePercent = budgetHours !== 0 ? (hoursVariance / budgetHours) * 100 : 0;

    // GP (simple method for the raw data — page component recalculates with chosen method)
    const budgetGPResult = calculateBudgetGP({ revenue: contractValue, totalDirectCosts: totalBudgetCost }, 'simple');
    const actualGPResult = calculateGP({
      revenue: invoicedRevenue,
      totalDirectCosts: totalActualCost,
      labourCosts: project.actualLabourCosts,
      subcontractorCosts: project.actualSubcontractorCosts,
      overheadAbsorption: project.actualOverheadAbsorption,
    }, 'simple');

    // Resolve PM name
    const pmFromRoles = project.roleAssignments?.find(
      (r: any) => r.role === 'project-manager'
    )?.employeeName;
    const pmName = pmFromRoles ?? (
      mockData.mockEmployees?.find((e: any) => e.id === project.projectManagerId)?.name ?? '—'
    );

    const result: import('@/types/reports.types').ProjectPLReport = {
      projectId: project.id,
      projectName: project.name,
      projectCode: project.code ?? '',
      clientName: project.clientNames ?? '—',
      projectType: project.type ?? 'time-and-materials',
      projectManager: pmName,
      status: project.status ?? String(project.statusId ?? ''),
      startDate: project.startDate ?? '',
      endDate: project.endDate ?? '',
      completionPercent: project.completionPercentOverride ?? project.completionPercent ?? 0,
      revenue: {
        contractValue,
        invoicedRevenue,
        recognisedRevenue: null,
        revenueVariance,
        revenueVariancePercent,
      },
      costs: {
        totals: {
          budget: totalBudgetCost,
          actual: totalActualCost,
          variance: costVariance,
          variancePercent: costVariancePercent,
        },
        categories,
        simplifiedFallback: false,
      },
      grossProfit: {
        budgetGP: budgetGPResult.grossProfit,
        actualGP: actualGPResult.grossProfit,
        gpVariance: actualGPResult.grossProfit - budgetGPResult.grossProfit,
        gpVariancePercent: budgetGPResult.grossProfit !== 0 ? ((actualGPResult.grossProfit - budgetGPResult.grossProfit) / budgetGPResult.grossProfit) * 100 : 0,
        budgetMarginPct: budgetGPResult.grossMarginPct,
        actualMarginPct: actualGPResult.grossMarginPct,
        marginVariancePp: actualGPResult.grossMarginPct - budgetGPResult.grossMarginPct,
        gpMethodUsed: 'simple',
      },
      hours: {
        budget: budgetHours,
        actual: actualHours,
        variance: hoursVariance,
        variancePercent: hoursVariancePercent,
      },
      actualLabourCosts: project.actualLabourCosts,
      actualSubcontractorCosts: project.actualSubcontractorCosts,
      actualOverheadAbsorption: project.actualOverheadAbsorption,
      costBreakdownAvailable: true,
    };

    return result;
  }

  // Live mode — call the real endpoint
  const baseUrl = getApiBaseUrl();
  const response = await apiFetch<import('@/types/reports.types').ProjectPLReport>(
    `${baseUrl}/reports/project-pl/${projectId}`
  );
  return response;
}

// ── Project Forecast Report ─────────────────────────────────────────────────

/**
 * Fetches per-project forecast data for the Project Forecast Report.
 *
 * Demo mode: builds from `mockProjects` + `getMockBudgetData()`. Returns the
 * same shape as the live endpoint so the report page works identically in both
 * modes.
 *
 * Live mode: calls `GET /api/reports/project-forecast` and returns the
 * `projects` array from the response.
 */
export async function fetchProjectForecastData(): Promise<{
  projects: import('@/types/reports.types').ProjectForecastData[];
  summary: import('@/types/reports.types').ForecastSummary;
}> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 300));

    const projects = mode === 'demo' ? mockData.mockProjects : (tourMockData as any).tourMockProjects ?? mockData.mockProjects;
    const thresholds = getBudgetHealthThresholds();

    let totalBudgetCost = 0;
    let atRiskCount = 0;

    const forecastProjects: import('@/types/reports.types').ProjectForecastData[] = projects
      .filter((p: any) => {
        const statusId = p.statusId ?? 1;
        return statusId === 1 || statusId === 2; // Planning, In Progress
      })
      .map((project: any) => {
        const budgetData = getMockBudgetData(project.id);
        const costBudgetTotal = budgetData?.costBudget?.totals?.budget ?? 0;
        const actualCosts = (project.actualLabour ?? 0) + (project.actualExpense ?? 0);
        const completionPercent = computeEffectiveCompletionPercent(project);

        totalBudgetCost += costBudgetTotal;

        // Derive health
        const health = deriveProjectHealth(
          {
            actualCost: actualCosts,
            budgetCost: costBudgetTotal,
            startDate: project.startDate,
            endDate: project.endDate,
            completionPercent,
            actualHours: project.actualHours ?? 0,
            budgetHours: project.budgetHours ?? 0,
            wipAmount: project.wipAmount ?? 0,
            actualRevenue: project.actualRevenue ?? 0,
            budgetRevenue: project.budgetRevenue ?? 0,
            forecastEndDate: project.forecastEndDate,
          },
          thresholds
        );

        if (health === 'amber' || health === 'red') {
          atRiskCount++;
        }

        // Resolve PM name
        const pmFromRoles = project.roleAssignments?.find(
          (r: any) => r.role === 'project-manager'
        )?.employeeName;
        const pmName = pmFromRoles ?? (
          mockData.mockEmployees?.find((e: any) => e.id === project.projectManagerId)?.name ?? '—'
        );

        // Revenue data
        const projectedFinalRevenue = budgetData?.allBudgets?.forecast?.projectedFinalRevenue ?? null;

        // Hours — linear projection fallback
        const budgetHours = project.budgetHours ?? 0;
        const actualHours = project.actualHours ?? 0;
        const forecastAtCompletionHours = completionPercent > 0
          ? actualHours / (completionPercent / 100)
          : null;

        const entry: import('@/types/reports.types').ProjectForecastData = {
          projectId: project.id,
          projectName: project.name,
          projectCode: project.code ?? '',
          clientName: project.clientNames ?? '—',
          projectManager: pmName,
          statusId: project.statusId ?? 0,
          status: project.status ?? String(project.statusId ?? ''),
          health,
          billable: project.billable ?? true,
          completionPercent,
          startDate: project.startDate ?? '',
          endDate: project.endDate ?? '',
          forecastEndDate: project.forecastEndDate ?? null,
          forecastInputs: {
            actualCosts,
            totalBudget: costBudgetTotal,
            completionPercent,
          },
          revenueData: {
            budgetRevenue: project.budgetRevenue ?? 0,
            actualSales: project.actualSales ?? project.actualRevenue ?? 0,
            projectedFinalRevenue,
          },
          hoursData: {
            budgetHours,
            actualHours,
            forecastAtCompletionHours,
          },
        };

        return entry;
      });

    return {
      projects: forecastProjects,
      summary: {
        totalProjects: forecastProjects.length,
        totalBudgetCost,
        atRiskCount,
      },
    };
  }

  // Live mode — call the real endpoint
  const baseUrl = getApiBaseUrl();
  const raw = await apiFetch<{
    projects: Array<{
      projectId: number;
      projectName: string;
      projectCode: string;
      clientName: string | null;
      projectManager: string;
      statusId: number;
      status: string;
      health: string;
      completionPercent: number;
      startDate: string | null;
      endDate: string | null;
      forecastEndDate: string | null;
      actualCosts: number;
      totalBudget: number;
      budgetRevenue: number;
      actualSales: number;
      projectedFinalRevenue: number | null;
      budgetHours: number;
      actualHours: number;
      forecastAtCompletionHours: number | null;
    }>;
    summary: import('@/types/reports.types').ForecastSummary;
  }>(`${baseUrl}/reports/project-forecast`);

  const projects: import('@/types/reports.types').ProjectForecastData[] = raw.projects.map(r => ({
    projectId: r.projectId,
    projectName: r.projectName,
    projectCode: r.projectCode ?? '',
    clientName: r.clientName ?? '—',
    projectManager: r.projectManager ?? '—',
    statusId: r.statusId ?? 0,
    status: r.status ?? '',
    health: (['green', 'amber', 'red'].includes((r.health ?? '').toLowerCase())
      ? r.health.toLowerCase() as 'green' | 'amber' | 'red'
      : 'green'),
    completionPercent: r.completionPercent ?? 0,
    startDate: r.startDate ?? '',
    endDate: r.endDate ?? '',
    forecastEndDate: r.forecastEndDate ?? null,
    forecastInputs: {
      actualCosts: r.actualCosts ?? 0,
      totalBudget: r.totalBudget ?? 0,
      completionPercent: r.completionPercent ?? 0,
    },
    revenueData: {
      budgetRevenue: r.budgetRevenue ?? 0,
      actualSales: r.actualSales ?? 0,
      projectedFinalRevenue: r.projectedFinalRevenue ?? null,
    },
    hoursData: {
      budgetHours: r.budgetHours ?? 0,
      actualHours: r.actualHours ?? 0,
      forecastAtCompletionHours: r.forecastAtCompletionHours ?? null,
    },
  }));

  return { projects, summary: raw.summary };
}

/** Parameters for the paginated project forecast report endpoint. */
export interface ForecastReportParams {
  page?: number;
  pageSize?: number;
  status?: string;
  pm?: string;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  /** 'billable' | 'non-billable' — omit for all */
  billable?: string;
}

/**
 * Fetches a single page of project forecast data from the server.
 *
 * Demo mode: falls back to the full client-side dataset wrapped in a paginated
 * response shape so the page component can use the same code path.
 *
 * Live mode: calls `GET /api/reports/project-forecast?page=&pageSize=&…` which
 * returns only the requested page plus summary and filter options.
 */
export async function fetchProjectForecastDataPaged(
  params: ForecastReportParams = {},
): Promise<import('@/types/reports.types').ForecastPaginatedResponse> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    const full = await fetchProjectForecastData();
    const page = params.page ?? 1;
    const pageSize = params.pageSize ?? 25;

    let filtered = full.projects;

    if (params.status && params.status !== 'all') {
      if (params.status === 'active') {
        filtered = filtered.filter(p => p.statusId === 1 || p.statusId === 2);
      } else {
        const statusId = parseInt(params.status, 10);
        if (!isNaN(statusId)) {
          filtered = filtered.filter(p => p.statusId === statusId);
        } else {
          filtered = filtered.filter(p =>
            p.status.toLowerCase() === params.status!.toLowerCase());
        }
      }
    }

    if (params.pm && params.pm !== 'all')
      filtered = filtered.filter(p => p.projectManager === params.pm);

    if (params.search) {
      const q = params.search.toLowerCase();
      filtered = filtered.filter(p =>
        p.projectName.toLowerCase().includes(q) ||
        p.projectCode.toLowerCase().includes(q) ||
        p.clientName.toLowerCase().includes(q) ||
        (p.projectManager ?? '').toLowerCase().includes(q));
    }
    if (params.billable === 'billable') {
      filtered = filtered.filter(p => p.billable !== false);
    } else if (params.billable === 'non-billable') {
      filtered = filtered.filter(p => p.billable === false);
    }

    // Sort (limited keys — server paginates on raw rows, no EAC)
    const sortKey = params.sortBy ?? 'project';
    const asc = params.sortOrder === 'asc';
    filtered = [...filtered].sort((a, b) => {
      let diff = 0;
      switch (sortKey) {
        case 'project':
        case 'projectName':
          diff = a.projectName.localeCompare(b.projectName);
          break;
        case 'client':
        case 'clientName':
          diff = a.clientName.localeCompare(b.clientName);
          break;
        case 'budget':
        case 'totalBudget':
          diff = a.forecastInputs.totalBudget - b.forecastInputs.totalBudget;
          break;
        case 'actual':
        case 'actualCosts':
          diff = a.forecastInputs.actualCosts - b.forecastInputs.actualCosts;
          break;
        default:
          diff = a.projectName.localeCompare(b.projectName);
      }
      return asc ? diff : -diff;
    });

    // Summary
    const totalBudgetCost = filtered.reduce((s, p) => s + p.forecastInputs.totalBudget, 0);
    const atRiskCount = filtered.filter(p => p.health === 'amber' || p.health === 'red').length;

    const { paginateArray } = await import('@/types/pagination');
    const { data, pagination } = paginateArray(filtered, page, pageSize);

    return {
      projects: data,
      summary: {
        totalProjects: filtered.length,
        totalBudgetCost,
        atRiskCount,
      },
      pagination,
      filterOptions: {
        projectManagers: [...new Set(full.projects.map(p => p.projectManager).filter(Boolean))].sort(),
        statuses: [...new Set(full.projects.map(p => p.status).filter(Boolean))].sort(),
      },
    };
  }

  // Live mode — server-side pagination
  const baseUrl = getApiBaseUrl();
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);
  if (params.pm && params.pm !== 'all') searchParams.set('pm', params.pm);
  if (params.search) searchParams.set('search', params.search);
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

  const qs = searchParams.toString();
  const url = `${baseUrl}/reports/project-forecast${qs ? `?${qs}` : ''}`;

  const raw = await apiFetch<{
    projects: Array<{
      projectId: number;
      projectName: string;
      projectCode: string;
      clientName: string | null;
      projectManager: string;
      statusId: number;
      status: string;
      health: string;
      completionPercent: number;
      startDate: string | null;
      endDate: string | null;
      forecastEndDate: string | null;
      actualCosts: number;
      totalBudget: number;
      budgetRevenue: number;
      actualSales: number;
      projectedFinalRevenue: number | null;
      budgetHours: number;
      actualHours: number;
      forecastAtCompletionHours: number | null;
    }>;
    summary: import('@/types/reports.types').ForecastSummary;
    pagination: import('@/types/pagination').PaginationMetadata;
    filterOptions: import('@/types/reports.types').ForecastFilterOptions;
  }>(url, { credentials: 'include' });

  const projects: import('@/types/reports.types').ProjectForecastData[] = raw.projects.map(r => ({
    projectId: r.projectId,
    projectName: r.projectName,
    projectCode: r.projectCode ?? '',
    clientName: r.clientName ?? '—',
    projectManager: r.projectManager ?? '—',
    statusId: r.statusId ?? 0,
    status: r.status ?? '',
    health: (['green', 'amber', 'red'].includes((r.health ?? '').toLowerCase())
      ? r.health.toLowerCase() as 'green' | 'amber' | 'red'
      : 'green'),
    completionPercent: r.completionPercent ?? 0,
    startDate: r.startDate ?? '',
    endDate: r.endDate ?? '',
    forecastEndDate: r.forecastEndDate ?? null,
    forecastInputs: {
      actualCosts: r.actualCosts ?? 0,
      totalBudget: r.totalBudget ?? 0,
      completionPercent: r.completionPercent ?? 0,
    },
    revenueData: {
      budgetRevenue: r.budgetRevenue ?? 0,
      actualSales: r.actualSales ?? 0,
      projectedFinalRevenue: r.projectedFinalRevenue ?? null,
    },
    hoursData: {
      budgetHours: r.budgetHours ?? 0,
      actualHours: r.actualHours ?? 0,
      forecastAtCompletionHours: r.forecastAtCompletionHours ?? null,
    },
  }));

  return {
    projects,
    summary: raw.summary,
    pagination: raw.pagination,
    filterOptions: raw.filterOptions,
  };
}

// ── Budget Variance Report ──────────────────────────────────────────────────

/**
 * Fetches the Budget Variance Report data for a single project.
 *
 * Demo mode: reads from `getBudgetDataByProjectId()` and shapes the response
 * into a `BudgetVarianceReport` so the page works identically in both modes.
 *
 * Live mode: calls `GET /api/reports/budget-variance/{projectId}?revisionId={revisionId}`.
 */
export async function fetchBudgetVarianceData(
  projectId: number,
  revisionId?: number
): Promise<import('@/types/reports.types').BudgetVarianceReport | null> {
  const mode = getDataMode();

  if (mode === 'demo' || mode === 'tour') {
    await new Promise(resolve => setTimeout(resolve, 250));

    const projects = mode === 'demo' ? mockData.mockProjects : (tourMockData as any).tourMockProjects ?? mockData.mockProjects;
    const project = projects.find((p: any) => p.id === projectId);
    if (!project) return null;

    const budgetData = getMockBudgetData(projectId);
    if (!budgetData) return null;

    const costBudget = budgetData.costBudget;
    const revisions: import('@/types/budget.types').BudgetRevision[] = budgetData.revisions ?? [];
    const nonDraftRevisions = revisions.filter(r => r.status !== 'draft');

    // Resolve the selected revision for historical baseline
    const selectedRevision = revisionId
      ? nonDraftRevisions.find(r => r.id === revisionId) ?? null
      : null;

    // Build category budget overrides from historical revision snapshot
    const categoryBudgetOverrides = new Map<number, number>();
    if (selectedRevision?.costSnapshot) {
      for (const snap of selectedRevision.costSnapshot) {
        categoryBudgetOverrides.set(snap.categoryId, snap.budget);
      }
    }

    const totalBudget = selectedRevision?.costBudget ?? costBudget.totals.budget;
    const totalActual = costBudget.totals.actual;
    const totalCommitted = costBudget.totals.committed;
    const totalAvailable = totalBudget - totalActual - totalCommitted;
    const totalVariance = totalActual - totalBudget;
    const totalVariancePct = totalBudget !== 0 ? (totalVariance / totalBudget) * 100 : 0;

    // Map cost categories to BudgetVarianceCategory[]
    const categories: import('@/types/budget.types').BudgetVarianceCategory[] = costBudget.categories.map(cat => {
      const budget = categoryBudgetOverrides.get(cat.id) ?? cat.budget;
      const actual = cat.actual;
      const committed = cat.committed;
      const available = budget - actual - committed;
      const variance = actual - budget;
      const variancePct = budget !== 0 ? (variance / budget) * 100 : 0;
      const progressPct = budget > 0 ? (actual / budget) * 100 : 0;
      const thresholds = getBudgetHealthThresholds();
      const status: import('@/types/budget.types').BudgetStatus =
        progressPct >= thresholds.costRedThreshold ? 'red'
        : progressPct >= thresholds.costAmberThreshold ? 'amber'
        : 'green';

      const subcategories: import('@/types/budget.types').BudgetVarianceSubcategory[] | undefined =
        cat.subcategories?.map(sub => {
          const subVariance = sub.actual - sub.budget;
          const subVarPct = sub.budget !== 0 ? (subVariance / sub.budget) * 100 : 0;
          const subProgress = sub.budget > 0 ? (sub.actual / sub.budget) * 100 : 0;
          const subStatus: import('@/types/budget.types').BudgetStatus =
            subProgress >= thresholds.costRedThreshold ? 'red'
            : subProgress >= thresholds.costAmberThreshold ? 'amber'
            : 'green';
          return {
            id: sub.id,
            name: sub.name,
            budget: sub.budget,
            actual: sub.actual,
            committed: sub.committed,
            available: sub.budget - sub.actual - sub.committed,
            variance: subVariance,
            variancePercent: subVarPct,
            progressPercent: subProgress,
            status: subStatus,
          };
        });

      return {
        id: cat.id,
        name: cat.name,
        type: cat.type,
        budget,
        actual,
        committed,
        available,
        variance,
        variancePercent: variancePct,
        progressPercent: progressPct,
        status,
        subcategories,
      };
    });

    // Revenue
    const budgetRevenue = project.budgetRevenue ?? 0;
    const actualRevenue = project.actualSales ?? project.actualRevenue ?? 0;
    const revenueVariance = actualRevenue - budgetRevenue;
    const revenueVarPct = budgetRevenue !== 0 ? (revenueVariance / budgetRevenue) * 100 : 0;

    // Hours
    const budgetHours = project.budgetHours ?? 0;
    const actualHours = project.actualHours ?? 0;
    const hoursVariance = actualHours - budgetHours;
    const hoursVarPct = budgetHours !== 0 ? (hoursVariance / budgetHours) * 100 : 0;

    // Baseline
    const activeRevision = nonDraftRevisions.find(r => r.status === 'active' || r.status === 'current') ?? nonDraftRevisions[0];
    const baseline: import('@/types/budget.types').BudgetVarianceBaseline = selectedRevision
      ? {
          revisionId: selectedRevision.id,
          revisionLabel: `CB-v${selectedRevision.revisionNumber} — ${selectedRevision.reason}`,
          isActiveRevision: false,
          isOriginalBaseline: selectedRevision.isBaseline,
          date: selectedRevision.date,
        }
      : {
          revisionId: activeRevision?.id,
          revisionLabel: activeRevision
            ? `CB-v${activeRevision.revisionNumber} — ${activeRevision.reason}`
            : 'Active Budget',
          isActiveRevision: true,
          date: activeRevision?.date,
        };

    // Revision options for the dropdown
    const revisionOptions: import('@/types/budget.types').BudgetVarianceRevisionOption[] = nonDraftRevisions.map(r => ({
      id: r.id,
      label: `CB-v${r.revisionNumber} — ${r.reason}`,
      status: r.status,
      isActive: r.status === 'active' || r.status === 'current',
      isBaseline: r.isBaseline ?? false,
      date: r.date,
    }));

    const report: import('@/types/reports.types').BudgetVarianceReport = {
      projectId: project.id,
      projectName: project.name,
      projectCode: project.code ?? '',
      baseline,
      revenue: {
        budget: budgetRevenue,
        actual: actualRevenue,
        variance: revenueVariance,
        variancePercent: revenueVarPct,
      },
      costs: {
        totals: {
          budget: totalBudget,
          actual: totalActual,
          committed: totalCommitted,
          available: totalAvailable,
          variance: totalVariance,
          variancePercent: totalVariancePct,
        },
        categories,
      },
      hours: {
        budget: budgetHours,
        actual: actualHours,
        variance: hoursVariance,
        variancePercent: hoursVarPct,
      },
      revisions: revisionOptions,
    };

    return report;
  }

  // Live mode — call the real endpoint
  const baseUrl = getApiBaseUrl();
  const queryParams = revisionId ? `?revisionId=${revisionId}` : '';
  const response = await apiFetch<import('@/types/reports.types').BudgetVarianceReport>(
    `${baseUrl}/reports/budget-variance/${projectId}${queryParams}`
  );
  return response;
}

// ── Budget Configuration API ─────────────────────────────────────────────

export interface BudgetConfigurationPayload {
  budgetMode: string;
  costBudgetEnabled: boolean;
  salesBudgetEnabled: boolean;
  resourceBudgetEnabled: boolean;
  hasBudgetData?: boolean;
}

export async function fetchBudgetConfiguration(): Promise<BudgetConfigurationPayload> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<BudgetConfigurationPayload>(`${baseUrl}/settings/budget/configuration`);
}

export async function updateBudgetConfiguration(config: BudgetConfigurationPayload): Promise<void> {
  const baseUrl = getApiBaseUrl();
  await apiFetch<void>(`${baseUrl}/settings/budget/configuration`, {
    method: 'PUT',
    body: JSON.stringify(config),
  });
}

// ── Cost Centre Budgets API ──────────────────────────────────────────────

export interface CostCentreBudgetEntry {
  costCentreId: number;
  costCentreCode?: string;
  budgetType: 'cost' | 'sales' | 'resource';
  monthlyBudgets: Record<string, number>;
}

// Short-lived in-flight de-duplication for cost-centre budget fetches. In Cost
// Centre mode the same project/budgetType is requested concurrently by both the
// budget tab and its edit form, producing duplicate network requests. Concurrent
// callers share a single in-flight request keyed by projectId + budgetType; the
// entry is cleared once it settles so later (post-mutation) fetches still hit the
// network for fresh data.
const costCentreBudgetsInflight = new Map<string, Promise<CostCentreBudgetEntry[]>>();

export async function fetchCostCentreBudgetsByProject(
  projectId: number,
  budgetType?: 'cost' | 'sales' | 'resource'
): Promise<CostCentreBudgetEntry[]> {
  const key = `${projectId}:${budgetType ?? 'all'}`;
  const inflight = costCentreBudgetsInflight.get(key);
  if (inflight) {
    return inflight;
  }

  const baseUrl = getApiBaseUrl();
  const queryParams = budgetType ? `?budgetType=${budgetType}` : '';
  const request = apiFetch<CostCentreBudgetEntry[]>(
    `${baseUrl}/CostCentreBudgets/by-project/${projectId}${queryParams}`
  ).finally(() => {
    costCentreBudgetsInflight.delete(key);
  });
  costCentreBudgetsInflight.set(key, request);
  return request;
}

export async function upsertCostCentreBudget(
  costCentreId: number,
  budgetType: 'cost' | 'sales' | 'resource',
  monthlyBudgets: Record<string, number>
): Promise<void> {
  const baseUrl = getApiBaseUrl();
  await apiFetch<void>(`${baseUrl}/CostCentreBudgets/${costCentreId}`, {
    method: 'PUT',
    body: JSON.stringify({ budgetType, monthlyBudgets }),
  });
}

export async function fetchCostCentresWithBudgets(projectId: number): Promise<any[]> {
  const baseUrl = getApiBaseUrl();
  return apiFetch<any[]>(`${baseUrl}/CostCentres/by-project/${projectId}/with-budgets`);
}

// ── Monthly Financial Summary (BE-1) ──────────────────────────────────

export interface MonthlyFinancialSummary {
  periodNumber: number;
  yearLabel: string;
  monthLabel: string;
  cumulativeSales: number;
  cumulativeCost: number;
  periodSales: number;
  periodCost: number;
  grossProfit: number;
  grossMarginPercent: number;
  actualHours: number;
  cumulativeHours: number;
}

export async function fetchProjectMonthlySummary(
  projectCode: string,
  dateFrom?: string,
  dateTo?: string,
): Promise<MonthlyFinancialSummary[]> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return [];
  }
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  const qs = params.toString();
  return apiFetch<MonthlyFinancialSummary[]>(
    `${getApiBaseUrl()}/projects/${encodeURIComponent(projectCode)}/financials/monthly-summary${qs ? `?${qs}` : ''}`
  );
}

export async function fetchPortfolioMonthlySummary(
  dateFrom?: string,
  dateTo?: string,
): Promise<MonthlyFinancialSummary[]> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return [];
  }
  const params = new URLSearchParams();
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  const qs = params.toString();
  return apiFetch<MonthlyFinancialSummary[]>(
    `${getApiBaseUrl()}/portfolio/financials/monthly-summary${qs ? `?${qs}` : ''}`
  );
}

// ── Cost Rollup by Category (BE-4) ────────────────────────────────────

export interface CategoryCostRollup {
  categoryId: number;
  categoryName: string;
  categoryType: string;
  budgetAmount: number;
  actualAmount: number;
  nominalCodes: string[];
}

export async function fetchCostRollupByCategory(
  projectId: number,
): Promise<CategoryCostRollup[]> {
  const mode = getDataMode();
  if (mode === 'demo') {
    return [];
  }
  return apiFetch<CategoryCostRollup[]>(
    `${getApiBaseUrl()}/projects/${projectId}/cost-rollup-by-category`
  );
}
